import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useAllOffers } from '../../api/api.calls';
import { get } from 'lodash';

export const useFilteredData = () => {
  const { data: apiResponse } = useAllOffers();
  const offersList = useMemo(() => {
    return get(apiResponse, 'data', []) ?? [];
  }, [apiResponse]);
  const appliedFilters =
    useSelector(({ appReducer }) => appReducer.appliedFilters) || {};
  const { platform = 'all' } = appliedFilters || {};
  const platformValue = platform;
  const memoizedOffersList = useMemo(() => {
    return (
      offersList.filter((entry) =>
        platformValue !== 'all' ? entry.type === platformValue : entry,
      ) || []
    );
  }, [offersList, platformValue]);

  // Apply Main Filter
  const filteredOnCategory = useMemo(() => {
    const categoryApplied = appliedFilters?.category ?? [];
    const filteredMain = memoizedOffersList.filter((entry) =>
      categoryApplied?.includes(entry.category_id),
    );
    const filteredObjects = memoizedOffersList.filter((item) =>
      item.applicable_brand_logos.category_ids?.some((categoryId) =>
        categoryApplied?.includes(categoryId),
      ),
    );
    const filteredGlobal = memoizedOffersList.filter(
      (entry) => categoryApplied?.length && entry.category_id === 49, //GLOBAL CATEGORY ID
    );
    let filtered = [...filteredMain, ...filteredObjects, ...filteredGlobal];
    filtered = Array.from(
      new Set(filtered.map((obj) => JSON.stringify(obj))),
    ).map((str) => JSON.parse(str));
    // Return all if not filters Selected
    const fallback = appliedFilters?.category?.length ? [] : memoizedOffersList;
    return filtered.length ? filtered : fallback;
  }, [appliedFilters.category, memoizedOffersList]);

  // Apply Sub Filter
  const filterOnSubCategory = useMemo(() => {
    const filteredMain = filteredOnCategory.filter((entry) => {
      return appliedFilters?.brand?.includes(entry.brand_id);
    });
    const filteredObjects = memoizedOffersList.filter((item) =>
      item.applicable_brand_logos.brand_ids?.some((brandIds) => {
        return appliedFilters.brand?.includes(Number(brandIds));
      }),
    );
    const filteredGlobal = filteredOnCategory.filter(
      (entry) => appliedFilters?.brand?.length && entry.brand_id === 1,
    );
    let filtered = [...filteredMain, ...filteredObjects, ...filteredGlobal];
    filtered = Array.from(
      new Set(filtered.map((obj) => JSON.stringify(obj))),
    ).map((str) => JSON.parse(str));

    // Return filtered on Category
    return filtered?.length
      ? filtered
      : appliedFilters.brand?.length
        ? []
        : filteredOnCategory;
  }, [appliedFilters.brand, filteredOnCategory, memoizedOffersList]);

  const filterOnOfferFilterType = useMemo(() => {
    const filtered = filterOnSubCategory.filter((entry) =>
      appliedFilters?.offer_filter_type?.includes(entry.offer_filter_type),
    );
    // Return filtered on Category
    return filtered?.length
      ? filtered
      : appliedFilters.offer_filter_type?.length
        ? []
        : filterOnSubCategory;
  }, [appliedFilters.offer_filter_type, filterOnSubCategory]);

  // Apply offer_tag
  const filterOnOfferTag = useMemo(() => {
    const filtered = filterOnOfferFilterType.filter((entry) =>
      appliedFilters?.offer_type?.includes(entry.offer_tag),
    );
    // Return filtered on Category
    return filtered?.length
      ? filtered
      : appliedFilters.offer_type?.length
        ? []
        : filterOnOfferFilterType;
  }, [appliedFilters.offer_type, filterOnOfferFilterType]);

  // Apply reward_offer
  const filterOnIssuer = useMemo(() => {
    const filtered = filterOnOfferTag.filter((entry) =>
      appliedFilters?.reward_offer?.includes(entry.brand_id),
    );
    // Return filtered on Category
    return filtered?.length
      ? filtered
      : appliedFilters.reward_offer?.length
        ? []
        : filterOnOfferTag;
  }, [appliedFilters.reward_offer, filterOnOfferTag]);

  // Apply offer_value
  const filterOnOfferValue = useMemo(() => {
    const offerValue = Number(
      appliedFilters?.offer_value?.length ? appliedFilters?.offer_value[0] : 0,
    );
    const filtered = filterOnIssuer.filter((entry) => {
      return offerValue === 0 ? entry : offerValue <= entry.offer_value;
    });
    // Return filtered on Category
    return filtered?.length
      ? filtered
      : appliedFilters.offer_value?.length
        ? []
        : filterOnIssuer;
  }, [appliedFilters.offer_value, filterOnIssuer]);

  return {
    filterOnIssuer,
    filterOnOfferTag,
    filterOnSubCategory,
    filteredOnCategory,
    filterOnOfferValue,
    filterOnOfferFilterType,
  };
};
